import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

const LessonQuiz = ({ courseType, lessonNumber, onQuizPassed, onClose }) => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [alreadyPassed, setAlreadyPassed] = useState(false);

  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuiz();
  }, [courseType, lessonNumber]);

  const fetchQuiz = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const url = `${API}/quiz/${courseType}/lesson/${lessonNumber}`;
      console.log('Fetching quiz from:', url);
      
      const response = await axios({
        method: 'get',
        url: url,
        withCredentials: true,
        timeout: 10000
      });
      
      console.log('Quiz response:', response.data);
      
      if (response.data && response.data.quiz && response.data.quiz.questions) {
        setQuiz(response.data.quiz);
        setAlreadyPassed(response.data.already_passed || false);
        // Initialize answers
        const initialAnswers = {};
        response.data.quiz.questions.forEach((_, idx) => {
          initialAnswers[idx] = null;
        });
        setAnswers(initialAnswers);
      } else {
        setError('Quiz data not found. Please try again.');
      }
    } catch (err) {
      console.error('Failed to fetch quiz:', err);
      if (err.response) {
        // Server responded with error
        setError(err.response.data?.detail || `Server error: ${err.response.status}`);
      } else if (err.request) {
        // No response received
        setError('Could not connect to server. Please check your connection.');
      } else {
        // Request setup error
        setError(err.message || 'Failed to load quiz.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const unanswered = Object.values(answers).filter(a => a === null).length;
    if (unanswered > 0) {
      alert(`Please answer all questions! ${unanswered} question(s) remaining.`);
      return;
    }

    try {
      setSubmitting(true);
      const answerArray = Object.keys(answers)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(key => answers[key]);

      const response = await axios.post(
        `${API}/quiz/${courseType}/lesson/${lessonNumber}/submit`,
        {
          course_type: courseType,
          lesson_number: lessonNumber,
          answers: answerArray
        },
        { withCredentials: true }
      );

      setResult(response.data);
      
      if (response.data.passed && onQuizPassed) {
        onQuizPassed(response.data);
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      alert('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setResult(null);
    setCurrentQuestion(0);
    const resetAnswers = {};
    quiz.questions.forEach((_, idx) => {
      resetAnswers[idx] = null;
    });
    setAnswers(resetAnswers);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-8 max-w-2xl w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading Quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-red-900 to-orange-900 rounded-2xl p-8 max-w-2xl w-full text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-4">Quiz Loading Error</h2>
          <p className="text-red-200 mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={fetchQuiz}
              className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-xl font-bold hover:bg-yellow-400"
            >
              🔄 Try Again
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-white/20 text-white rounded-xl font-bold hover:bg-white/30"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (alreadyPassed && !result) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-green-900 to-emerald-900 rounded-2xl p-8 max-w-2xl w-full text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h2 className="text-2xl font-bold text-white mb-4">Quiz Already Passed!</h2>
          <p className="text-green-200 mb-6">
            You've already mastered this lesson. The next lesson is unlocked and your PDF workbook is available!
          </p>
          <button
            onClick={onClose}
            className="px-8 py-3 bg-white text-green-700 rounded-xl font-bold hover:bg-gray-100 transition-all"
          >
            Continue Learning
          </button>
        </div>
      </div>
    );
  }

  // Show results
  if (result) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className={`${result.passed ? 'bg-gradient-to-br from-green-900 to-emerald-900' : 'bg-gradient-to-br from-orange-900 to-red-900'} rounded-2xl p-8 max-w-3xl w-full my-8`}>
          {/* Result Header */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">{result.passed ? '🎉' : '📚'}</div>
            <h2 className="text-3xl font-bold text-white mb-2">
              {result.passed ? 'Congratulations!' : 'Keep Learning!'}
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className={`text-5xl font-bold ${result.passed ? 'text-green-400' : 'text-orange-400'}`}>
                {result.score}%
              </div>
              <div className="text-left">
                <p className="text-white/80 text-sm">Your Score</p>
                <p className="text-white/60 text-xs">Need {result.passing_score}% to pass</p>
              </div>
            </div>
            <p className="text-white/90">{result.message}</p>
          </div>

          {/* Results breakdown */}
          <div className="space-y-4 mb-8 max-h-96 overflow-y-auto">
            {result.results.map((r, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-xl ${r.is_correct ? 'bg-green-500/20 border border-green-400/30' : 'bg-red-500/20 border border-red-400/30'}`}
              >
                <div className="flex items-start gap-3">
                  <span className={`text-2xl ${r.is_correct ? '' : ''}`}>
                    {r.is_correct ? '✅' : '❌'}
                  </span>
                  <div className="flex-1">
                    <p className="text-white font-medium mb-2">{r.question}</p>
                    <p className={`text-sm ${r.is_correct ? 'text-green-300' : 'text-red-300'}`}>
                      {r.is_correct ? 'Correct!' : `Your answer was wrong.`}
                    </p>
                    <p className="text-white/70 text-sm mt-2 italic">
                      💡 {r.explanation}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex gap-4 justify-center">
            {result.passed ? (
              <>
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-white text-green-700 rounded-xl font-bold hover:bg-gray-100 transition-all"
                >
                  🚀 Continue to Next Lesson
                </button>
                {result.pdf_unlocked && (
                  <button
                    onClick={onClose}
                    className="px-8 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-400 transition-all"
                  >
                    📥 Download Workbook
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={handleRetry}
                  className="px-8 py-3 bg-yellow-500 text-gray-900 rounded-xl font-bold hover:bg-yellow-400 transition-all"
                >
                  🔄 Try Again
                </button>
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-white/20 text-white rounded-xl font-bold hover:bg-white/30 transition-all"
                >
                  Review Lesson
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quiz questions view
  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-red-900 to-orange-900 rounded-2xl p-8 max-w-2xl w-full text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-4">Quiz Not Available</h2>
          <p className="text-red-200 mb-6">The quiz questions could not be loaded. Please try again or contact support.</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={fetchQuiz}
              className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-xl font-bold hover:bg-yellow-400"
            >
              🔄 Reload Quiz
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-white/20 text-white rounded-xl font-bold hover:bg-white/30"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];
  const totalQuestions = quiz.questions.length;
  const answeredCount = Object.values(answers).filter(a => a !== null).length;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">{quiz?.title}</h2>
            <p className="text-purple-200 text-sm">Pass with {quiz?.passing_score}% to unlock next lesson</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-white/80 mb-2">
            <span>Question {currentQuestion + 1} of {totalQuestions}</span>
            <span>{answeredCount} answered</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        {question && (
          <div className="mb-8">
            <h3 className="text-xl text-white font-medium mb-6">
              {question.question}
            </h3>
            
            <div className="space-y-3">
              {question.options.map((option, optIdx) => (
                <button
                  key={optIdx}
                  onClick={() => handleAnswerSelect(currentQuestion, optIdx)}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-200 ${
                    answers[currentQuestion] === optIdx
                      ? 'bg-cyan-500 text-white border-2 border-cyan-300 shadow-lg shadow-cyan-500/30'
                      : 'bg-white/10 text-white border-2 border-transparent hover:bg-white/20 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      answers[currentQuestion] === optIdx
                        ? 'bg-white text-cyan-600'
                        : 'bg-white/20 text-white'
                    }`}>
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    <span className="flex-1">{option}</span>
                    {answers[currentQuestion] === optIdx && (
                      <span className="text-xl">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              currentQuestion === 0
                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            ← Previous
          </button>

          {/* Question dots */}
          <div className="flex gap-2">
            {quiz?.questions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentQuestion
                    ? 'bg-cyan-400 scale-125'
                    : answers[idx] !== null
                      ? 'bg-green-400'
                      : 'bg-white/30'
                }`}
              />
            ))}
          </div>

          {currentQuestion < totalQuestions - 1 ? (
            <button
              onClick={() => setCurrentQuestion(prev => Math.min(totalQuestions - 1, prev + 1))}
              className="px-6 py-3 bg-white/20 text-white rounded-xl font-semibold hover:bg-white/30 transition-all"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || answeredCount < totalQuestions}
              className={`px-8 py-3 rounded-xl font-bold transition-all ${
                submitting || answeredCount < totalQuestions
                  ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 hover:from-yellow-300 hover:to-orange-400 shadow-lg'
              }`}
            >
              {submitting ? 'Submitting...' : '✨ Submit Quiz'}
            </button>
          )}
        </div>

        {/* Submit hint */}
        {answeredCount < totalQuestions && (
          <p className="text-center text-yellow-300/80 text-sm mt-4">
            Answer all {totalQuestions} questions to submit
          </p>
        )}
      </div>
    </div>
  );
};

export default LessonQuiz;
